import React, { useState, useEffect, useMemo } from 'react'
import { Layout } from '../../components/Layout/Layout'
import { Modal } from '../../components/Modal/Modal'
import { UserForm } from './UserForm'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { exportRowsAsCsv } from '../../utils/csv'
import { T, useContent } from '../../content/ContentProvider'

export function Users() {
  const { t } = useContent()
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')

  useEffect(() => {
    fetchUsers()
  }, [])

  const fetchUsers = async () => {
    setLoading(true)
    setError('')
    try {
      const r = await api.get('/api/admin/users?size=100')
      const data = Array.isArray(r.data) ? r.data : r.data.content || []
      setUsers(data)
    } catch (err) {
      setError(err.response?.data?.message || t('users.error.load'))
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (user = null) => {
    setSelectedUser(user)
    setIsModalOpen(true)
  }
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedUser(null)
  }
  const handleSubmit = async (formData) => {
    setIsSubmitting(true)
    try {
      if (selectedUser?.id) {
        await api.put(`/api/admin/users/${selectedUser.id}`, formData)
      } else {
        await api.post('/api/admin/users', formData)
      }
      await fetchUsers()
      handleCloseModal()
    } catch (err) {
      setError(err.response?.data?.message || t('users.error.save'))
    } finally {
      setIsSubmitting(false)
    }
  }
  const handleDelete = async (e, id) => {
    e.stopPropagation()
    if (!confirm(t('users.confirmDelete'))) return
    try {
      await api.delete(`/api/admin/users/${id}`)
      await fetchUsers()
    } catch (err) {
      setError(err.response?.data?.message || t('users.error.delete'))
    }
  }

  const filtered = useMemo(() => {
    let r = users
    if (roleFilter !== 'all') r = r.filter((u) => (u.role || '').toUpperCase() === roleFilter)
    if (search.trim()) {
      const s = search.toLowerCase()
      r = r.filter(
        (u) =>
          (u.username || '').toLowerCase().includes(s) ||
          (u.email || '').toLowerCase().includes(s) ||
          (u.firstName || '').toLowerCase().includes(s) ||
          (u.lastName || '').toLowerCase().includes(s) ||
          (u.company?.name || '').toLowerCase().includes(s)
      )
    }
    return r
  }, [users, search, roleFilter])

  const counts = {
    all: users.length,
    ADMIN: users.filter((u) => (u.role || '').toUpperCase() === 'ADMIN').length,
    USER: users.filter((u) => (u.role || '').toUpperCase() === 'USER').length,
  }

  return (
    <Layout crumb={t('users.crumb')}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div>
              <h1 className="page-head__h"><T k="users.title" /></h1>
              <div className="page-head__sub">
                <T k="users.subtitle" vars={{ count: users.length, companies: new Set(users.map((u) => u.company?.id).filter(Boolean)).size }} />
              </div>
            </div>
            <div className="page-head__actions">
              <button
                className="btn"
                onClick={() =>
                  exportRowsAsCsv(
                    users,
                    [
                      { key: 'id', label: t('users.csv.id') },
                      { key: 'username', label: t('users.csv.username') },
                      { key: 'firstName', label: t('users.csv.firstName') },
                      { key: 'lastName', label: t('users.csv.lastName') },
                      { key: 'email', label: t('users.csv.email') },
                      { key: 'role', label: t('users.csv.role') },
                      { label: t('users.csv.company'), get: (u) => u.company?.name || '' },
                      { label: t('users.csv.rics'), get: (u) => u.company?.rics || '' },
                      { key: 'status', label: t('users.csv.status') },
                    ],
                    'mas-users'
                  )
                }
              >
                {Icon.download({ s: 12 })} {t('users.action.export')}
              </button>
              <button className="btn btn--primary" onClick={() => handleOpenModal()}>
                {Icon.upload({ s: 12 })} {t('users.action.new')}
              </button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {error && (
          <div
            className="status-msg status-msg--err"
            role="alert"
            aria-live="polite"
            style={{
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        <div className="card">
          <div className="tab-bar">
            {[
              ['all', t('users.tab.all'), counts.all],
              ['ADMIN', t('users.tab.admins'), counts.ADMIN],
              ['USER', t('users.tab.users'), counts.USER],
            ].map(([k, l, c]) => (
              <button
                key={k}
                className={'tab-bar__tab ' + (roleFilter === k ? 'is-active' : '')}
                onClick={() => setRoleFilter(k)}
              >
                {l} <span className="count">{c}</span>
              </button>
            ))}
          </div>

          <div className="filter-bar">
            <div className="search-input">
              {Icon.search({ s: 12 })}
              <input
                placeholder={t('users.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11.5, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
              <T k="users.countOf" vars={{ shown: filtered.length, total: users.length }} />
            </span>
          </div>

          <div className="tbl-wrap">
            <table className="tbl table--cards">
              <thead>
                <tr>
                  <th>{t('users.table.username')}</th>
                  <th>{t('users.table.name')}</th>
                  <th>{t('users.table.email')}</th>
                  <th>{t('users.table.company')}</th>
                  <th>{t('users.table.role')}</th>
                  <th>{t('users.table.status')}</th>
                  <th style={{ width: 24 }}></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      <T k="users.loading" />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      <T k="users.empty" />
                    </td>
                  </tr>
                ) : (
                  filtered.map((u) => (
                    <tr key={u.id} onClick={() => handleOpenModal(u)} style={{ cursor: 'pointer' }}>
                      <td className="id bold" data-label={t('users.table.username')}>{u.username}</td>
                      <td data-label={t('users.table.name')}>{[u.firstName, u.lastName].filter(Boolean).join(' ') || '—'}</td>
                      <td className="muted" data-label={t('users.table.email')}>{u.email}</td>
                      <td className="muted" data-label={t('users.table.company')}>{u.company?.name || u.companyName || '—'}</td>
                      <td data-label={t('users.table.role')}>
                        <span className={'pill pill--' + (u.role === 'ADMIN' ? 'ok' : 'neutral')}>
                          <span className="pill-dot" />
                          {u.role}
                        </span>
                      </td>
                      <td data-label={t('users.table.status')}>
                        <span className={'pill pill--' + (u.enabled ? 'ok' : 'err')}>
                          <span className="pill-dot" />
                          {u.enabled ? <T k="users.status.active" /> : <T k="users.status.disabled" />}
                        </span>
                      </td>
                      <td data-label={t('users.table.actions')}>
                        <button className="iconbtn" style={{ width: 24, height: 24 }} onClick={(e) => handleDelete(e, u.id)}>
                          {Icon.close({ s: 13 })}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>

        <Modal
          isOpen={isModalOpen}
          title={selectedUser ? t('users.modal.edit') : t('users.modal.create')}
          onClose={handleCloseModal}
        >
          <UserForm user={selectedUser} onSubmit={handleSubmit} isLoading={isSubmitting} />
        </Modal>
      </div>
    </Layout>
  )
}
